import { useState, useRef, useEffect, useMemo, useCallback, type CSSProperties } from 'react'
import { LayoutGrid, FolderOpen, Share2, MoreVertical, Wrench, Play, User, Shield } from 'lucide-react'
import { ManifestProblemBadge } from '../ManifestProblemBadge'
import { parseMiniappIcon, parseMiniappIconRev, appDisplayName } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { Tooltip, MiniappIcon, avatarBg, getInitials, useOrgMemberNames, useTheme, getThemeClass, FileContextMenu, useAnchoredPosition, type FileMenuItem } from '@papan/core-ui/ui'
import { ProjectAccessLine } from '../AccessSummaryBits'
import { buildProjectMenuItems } from './projectMenu'
import { formatRelativeDate } from '../formatters'
import { projectDisplayName, projectHue, type ProjectItem } from './data'

export type ProjectCardVariant = 'project' | 'miniapp'
export type ProjectDateMode = 'modified' | 'created'

/** The actions a card can raise — the view owns the heavy dialogs. */
export interface ProjectCardHandlers {
  onOpen: (p: ProjectItem) => void
  onBrowse: (p: ProjectItem) => void
  /** Open the share-link dialog. Omitted on compact surfaces (Home strip). */
  onShare?: (p: ProjectItem) => void
  onWhoHasAccess?: (p: ProjectItem) => void
  /** "Share internally" — people, teams and roles of this org (the drive menu's own dialog). */
  onShareInternally?: (p: ProjectItem) => void
  /** "Share to another Drive" (cross-org). */
  onShareOrg?: (p: ProjectItem) => void
  /** Mini-apps: open the editor (Settings face). */
  onEditSettings?: (p: ProjectItem) => void
  /** Mini-apps: open the icon editor dialog. */
  onEditIcon?: (p: ProjectItem) => void
  // Organize / meta / danger — the view owns the dialogs (picker, rename modal,
  // details, debug log, delete confirm). Omitted → the menu item doesn't appear.
  onRename?: (p: ProjectItem) => void
  onDuplicate?: (p: ProjectItem) => void
  onMoveTo?: (p: ProjectItem) => void
  onCopyTo?: (p: ProjectItem) => void
  onDownload?: (p: ProjectItem) => void
  onDetails?: (p: ProjectItem) => void
  onDebugLog?: (p: ProjectItem) => void
  onDelete?: (p: ProjectItem) => void
  /** D118: open one file by id (an app's broken databases.json). Omitted → the warning stays a tooltip only. */
  onOpenFile?: (fileId: string) => void
}

/**
 * A project / mini-app tile for the Projects & MiniApps pages.
 *
 * Layout (top→bottom): glyph + name + creator avatar + share + ⋮ menu ·
 * date subtext (follows the page sort) · access line (always reserved) ·
 * Browse-files line. The `miniapp` variant renders the vivid app glyph + APP
 * badge + extra shortcuts (Run / wrench→Settings); the `project` variant is
 * always plain (even for a `.miniapp` folder — see #2 of the revamp).
 */
export function ProjectCard({ project, variant, dateMode, ...h }: {
  project: ProjectItem
  variant: ProjectCardVariant
  dateMode: ProjectDateMode
} & ProjectCardHandlers) {
  const { t } = useTranslation('files')
  const hue = projectHue(project.name)
  const isApp = variant === 'miniapp'
  // App cards hide `.miniapp`; project cards hide only `.project`.
  const displayName = isApp ? appDisplayName(project.name) : projectDisplayName(project.name)
  // metadataJson is the miniappIcon-stripped spec from the workspaces
  // endpoints (`.miniapp` rows only); null → name-derived default icon.
  const iconSpec = useMemo(() => parseMiniappIcon(project.metadataJson), [project.metadataJson])
  // The rev pairs with refId to load an image icon's server PNG; without it the
  // tile silently falls back to the name-derived gradient.
  const iconRev = useMemo(() => parseMiniappIconRev(project.metadataJson), [project.metadataJson])
  const owner = project.owner
  // Real names for the access-line grantees. One shared query across every
  // card and surface — see `useOrgMemberNames`; a card does not fetch.
  const { data: memberNames } = useOrgMemberNames()
  // Clicking the footer opens Who-has-access (personal + org — personal shows the
  // links/apps/outbound tabs). Anyone can VIEW it; management is server-gated.
  const canOpenAccess = !!h.onWhoHasAccess
  // Nothing special shared → the footer is blank; on hover, prompt "Manage".
  const acc = project.access
  const accessEmpty = !(
    (acc?.floor && acc.floor !== 'viewer') ||
    (acc?.teamCount ?? 0) > 0 || (acc?.peopleCount ?? 0) > 0 ||
    (acc?.links ?? 0) > 0 || (acc?.outsiders ?? 0) > 0
  )

  // The ⋮ menu uses the shared glass FileContextMenu (portaled to <body>).
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (!menu) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current?.contains(target) || btnRef.current?.contains(target)) return
      setMenu(null)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [menu])

  // Placement, follow-on-scroll and anchor-exit — the shared convention
  // (`useAnchoredPosition`). This card lives in a scrolling grid, so the menu
  // now rides with its card and plays out when the card leaves, instead of
  // being closed by the first wheel tick while still in plain sight.
  const { pos: menuPos, exiting: menuExiting, origin: menuOrigin } = useAnchoredPosition({
    open: !!menu,
    anchorRef: btnRef,
    floatingRef: menuRef,
    onDismiss: useCallback(() => setMenu(null), []),
    align: 'start',
    exitTransitionMs: 160,
  })

  const stop = (e: React.MouseEvent) => e.stopPropagation()
  const toggleMenu = (e: React.MouseEvent) => {
    stop(e)
    if (menu) { setMenu(null); return }
    setMenu({ x: 0, y: 0 })   // coordinates come from useAnchoredPosition
  }
  const dateVal = dateMode === 'created' ? project.createdAt : project.updatedAt
  const dateLabel = dateVal
    ? t(dateMode === 'created' ? 'driveHome.projectsPage.createdAgo' : 'driveHome.projectsPage.modifiedAgo', { when: formatRelativeDate(dateVal) })
    : '—'

  // The ⋮ menu items — the SAME catalog DriveView shows for a project / mini-app
  // folder (buildFileActions), rendered by the shared glass menu. Only actions
  // whose handler the card can wire appear; grouped with separators.
  const buildMenuItems = (): FileMenuItem[] => buildProjectMenuItems(project, h, { t, isApp, close: () => setMenu(null) })

  return (
    <div
      // #897 — what a phone/desktop layout swap keeps at the top of the page
      // (the grid has a different number of columns in each).
      data-scroll-anchor={project.folderId}
      onClick={() => h.onOpen(project)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') h.onOpen(project) }}
      className="ws-project-card group text-left cursor-pointer"
      style={{ '--pc-hue': hue } as CSSProperties}
      data-testid="project-card"
    >
      {/* Media object: badge (glyph + creator avatar overlay) · content column · ⋮. */}
      <div className="flex items-center gap-3">
        <div className="relative shrink-0 mx-1.5">
          {isApp ? (
            /* Mini-app: the icon tile — the "app" signal — rendered by the
               shared MiniappIcon from the stored spec (or name-derived default). */
            <MiniappIcon spec={iconSpec} name={displayName} size={40} refId={project.folderId} rev={iconRev} />
          ) : (
            <span aria-hidden className="ws-project-glyph">
              <LayoutGrid size={17} />
            </span>
          )}
          {/* Creator avatar — overlays the badge's bottom-right corner. */}
          {owner ? (
            <Tooltip
              always
              placement="top"
              className="absolute -bottom-1 -right-1"
              content={
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-wide opacity-70">{t('driveHome.projectsPage.createdByLabel', { defaultValue: 'Created by' })}</div>
                  <div className="font-medium">{owner.name?.trim() || owner.email}</div>
                  {owner.email && <div className="text-[10px] opacity-80">{owner.email}</div>}
                </div>
              }
            >
              <span aria-hidden className={`ws-card-owner-ring w-4 h-4 rounded-full inline-flex items-center justify-center text-[8px] font-semibold text-white ${avatarBg(owner.email || owner.name)}`}>
                {getInitials(owner.name)}
              </span>
            </Tooltip>
          ) : (
            <span aria-hidden className="ws-card-owner-ring ws-card-avatar-fallback absolute -bottom-1 -right-1 w-4 h-4 rounded-full inline-flex items-center justify-center themed-text-muted">
              <User size={9} />
            </span>
          )}
        </div>

        {/* Content column — title · date · icon buttons · access list (bottom). */}
        <div className="min-w-0 flex-1">
          <span className="block text-sm font-medium themed-text truncate">{displayName}</span>
          {/* D118 (owner 2026-10-20): its databases.json can't be read — its databases are held back (shown only to
              whoever can edit the app); a click opens the file */}
          {project.manifestProblem && (
            <ManifestProblemBadge problem={project.manifestProblem} {...(h.onOpenFile ? { onOpenFile: h.onOpenFile } : {})} className="inline-flex mt-0.5" />
          )}
          <span className="block text-[10px] themed-text-muted truncate mt-0.5">{dateLabel}</span>

          {/* Icon buttons — browse · share · (run · wrench). */}
          <div className="flex items-center gap-0.5 mt-1.5" onClick={stop}>
            <Tooltip always content={t('driveHome.projects.browseFiles')}>
              <button onClick={(e) => { stop(e); h.onBrowse(project) }} className="ws-card-iconbtn" aria-label={t('driveHome.projects.browseFiles')}>
                <FolderOpen size={14} />
              </button>
            </Tooltip>
            {h.onShare && (
              <Tooltip always content={t('driveView.menu.shareLink')}>
                <button onClick={(e) => { stop(e); h.onShare!(project) }} className="ws-card-iconbtn" aria-label={t('driveView.menu.shareLink')}>
                  <Share2 size={14} />
                </button>
              </Tooltip>
            )}
            {isApp && (
              <>
                <Tooltip always content={t('driveHome.projectsPage.runApp', { defaultValue: 'Run app' })}>
                  <button onClick={(e) => { stop(e); h.onOpen(project) }} className="ws-card-iconbtn" aria-label={t('driveHome.projectsPage.runApp', { defaultValue: 'Run app' })}>
                    <Play size={14} />
                  </button>
                </Tooltip>
                <Tooltip always content={t('driveView.menu.editApp', { defaultValue: 'Edit app' })}>
                  <button onClick={(e) => { stop(e); h.onEditSettings?.(project) }} className="ws-card-iconbtn" aria-label={t('driveView.menu.editApp', { defaultValue: 'Edit app' })}>
                    <Wrench size={14} />
                  </button>
                </Tooltip>
              </>
            )}
          </div>
        </div>

        {/* ⋮ — far right of the card, vertically centered. Glass context menu. */}
        <div className="shrink-0 self-center" onClick={stop}>
          <Tooltip always content={t('driveHome.projectsPage.moreActions', { defaultValue: 'More actions' })}>
            <button
              ref={btnRef}
              onClick={toggleMenu}
              className="ws-card-iconbtn"
              aria-label={t('driveHome.projectsPage.moreActions', { defaultValue: 'More actions' })}
              data-testid="project-card-menu"
            >
              <MoreVertical size={16} />
            </button>
          </Tooltip>
          {menu && (
            <FileContextMenu
              x={menuPos?.left ?? -9999}
              y={menuPos?.top ?? -9999}
              exiting={menuExiting}
              origin={menuOrigin}
              items={buildMenuItems()}
              menuRef={menuRef}
              className={themeClass}
              testId="project-card-context-menu"
            />
          )}
        </div>
      </div>

      {/* Full-width access footer — a lighter band across the card bottom. Breaks
          out of the card's 14px padding via negative margins; the card's
          overflow:hidden clips it to the rounded bottom corners. Clicking opens
          Who-has-access for org managers, else it's a plain reserved band. */}
      {canOpenAccess ? (
        <button
          onClick={(e) => { stop(e); h.onWhoHasAccess!(project) }}
          className="ws-project-card-footer group/foot block text-left -ml-3.5 -mb-3.5 mt-2.5 w-[calc(100%+28px)] px-3.5 py-1 transition-colors"
          aria-label={t('driveView.menu.whoHasAccess')}
          data-testid="project-card-access"
        >
          {accessEmpty ? (
            <span className="inline-flex items-center gap-1 min-h-[18px] text-[11px] themed-text-muted">
              <span className="inline-flex items-center gap-1 opacity-0 group-hover/foot:opacity-100 transition-opacity">
                <Shield size={12} /> {t('driveHome.projectsPage.manageAccess', { defaultValue: 'Manage' })}
              </span>
            </span>
          ) : (
            /* `creator` so a grantee who is the creator shows the same initials
               as the creator badge above — not email-derived ones. */
            <ProjectAccessLine access={project.access} creator={owner} memberNames={memberNames} t={t} />
          )}
        </button>
      ) : (
        <div className="ws-project-card-footer opacity-50 -ml-3.5 -mb-3.5 mt-2.5 w-[calc(100%+28px)] px-3.5 py-1" onClick={stop}>
          <ProjectAccessLine access={project.access} creator={owner} memberNames={memberNames} t={t} />
        </div>
      )}
    </div>
  )
}
